import { Vec2, Vec3, Vec4 } from "../../../models/SyntheticMap";
import { BaseVisual } from "../../../visuals/BaseVisual";
import { SiteAnchorVisual } from "../../../visuals/SiteAnchorVisual";
import { copyVec3, copyVec4, vec3Equal } from "../../../visuals/visual-utils";
import { WorldRenderer } from "../../WorldRenderer";
import { WREvents } from "../../WREvents";
import { BaseService } from "../BaseService";
import { PhysXObject, buildPhysXObject } from "./PhysXObject";
import { PWCameraUpdate, PWMessageDataUpdate, PWMessageInitResize, PWMessageType, PWScreenPosUpdate } from "./PhysXWorker";
import { PhysXWorkerProxy } from "./PhysXWorkerProxy";
import * as THREE from "three";
interface CheckTiming {
  name: string;
  interval: number; // Interval in seconds
  wait: number;
  check: () => void;
}

export class PhysXService extends BaseService {
  private proxy: PhysXWorkerProxy;
  private sceneSize: Vec2 = { x: 0, y: 0 };
  private cameraPosition: Vec3 = { x: 0, y: 0, z: 0 };
  private cameraLookAt: Vec3 = { x: 0, y: 0, z: 0 };
  private cameraUp: Vec3 = { x: 0, y: 0, z: 1 }; // Default up vector for the camera
  private ready = false;
  private checks: CheckTiming[] = [];
  public pendingActions = new Map<string, (() => void)>(); // Store pending actions to execute after loading
  private cameraUpdatesToPrime = 10;
  private receiver: any;
  private objects: Map<string, PhysXObject> = new Map<string, PhysXObject>();
  constructor(worldRenderer: WorldRenderer) {
    super(worldRenderer);
    this.receiver = {
      [PWMessageType.READY]: (message: any) => {
        this.ready = true;
      },
      [PWMessageType.SCREEN_POS_UPDATE]: (message: PWScreenPosUpdate) => {
        for (const update of message.changes) {
          const object = this.objects.get(update._id);
          if (object) {
            if (update.position) {
              object.screenPos = update.position;
            } else {
              object.screenPos = undefined;
            }
          }
        }
        this.worldRenderer.emitEvent(WREvents.ScreenPositionsUpdated, message.changes);
      },
      [PWMessageType.DATA_UPDATE]: (message: PWMessageDataUpdate) => {
        const { path, data } = message;
        if (this.messageHandlers.has(path)) {
          this.messageHandlers.get(path)!(data);
        }
      }
    };
    this.checks = [
      {
        name: 'resize check', interval: 1.4, wait: 0,
        check: () => {
          if (this.worldRenderer.sceneWidth !== this.sceneSize.x || this.worldRenderer.sceneHeight !== this.sceneSize.y) {
            this.sceneSize = { x: this.worldRenderer.sceneWidth, y: this.worldRenderer.sceneHeight };
            this.proxy.postMessage<PWMessageInitResize>({
              type: PWMessageType.RESIZE,
              data: this.sceneSize
            });
          }
        },
      },
      // {
      //   name: 'camera position check', interval: 0.1, wait: 0,
      //   check: () => {
      //     const cameraPos = this.worldRenderer.cameraControls.getPosition(new THREE.Vector3(), true);
      //     const cameraLookAt = this.worldRenderer.cameraControls.getTarget(new THREE.Vector3(), true);
      //     if (
      //       this.cameraUpdatesToPrime ||
      //       !vec3Equal(this.cameraPosition, cameraPos) ||
      //       !vec3Equal(this.cameraLookAt, cameraLookAt) ||
      //       !vec3Equal(this.cameraUp, this.worldRenderer.camera.up)
      //     ) {
      //       this.cameraUpdatesToPrime--;
      //       copyVec3(cameraPos, this.cameraPosition);
      //       copyVec3(cameraLookAt, this.cameraLookAt);
      //       copyVec3(this.worldRenderer.camera.up, this.cameraUp);
      //       this.proxy.postMessage<PWCameraUpdate>({
      //         type: PWMessageType.CAMERA_UPDATE,
      //         position: this.cameraPosition,
      //         target: this.cameraLookAt,
      //         up: this.cameraUp,
      //       });
      //     }
      //   },
      // }
    ]
    this.proxy = new PhysXWorkerProxy(this.receiver);
  }

  public async load(): Promise<void> {
    const wr = this.worldRenderer;
    this.sceneSize = { x: wr.sceneWidth, y: wr.sceneHeight };
    this.proxy.postMessage<PWMessageInitResize>({
      type: PWMessageType.INIT,
      data: this.sceneSize
    });
    // const testPlanet = PhysXPlanet.createSphereMesh();
    // wr.scene.add(testPlanet);
  }

  public updateObjects(visuals: BaseVisual[]): void {
    this.proxy.postMessage({
      type: PWMessageType.OBJECTS_UPDATE,
      objects: visuals.map(visual => visual.getPhysXObject())
    });
  }

  public replaceObjects(visuals: BaseVisual[]): void {
    this.objects.clear();
    for (const visual of visuals) {
      const physXObject = visual.getPhysXObject();
      // if (visual instanceof SiteAnchorVisual) {
      //   debugger;
      // }
      this.objects.set(physXObject._id, physXObject);
    }

    this.pendingActions.set('replaceObjects', () => {
      this.proxy.postMessage({
        type: PWMessageType.OBJECTS_REPLACE,
        objects: Array.from(this.objects.values())
      });
    });
  }


  render(delta: number): void {
    if (!this.ready) {
      return;
    }
    for (const check of this.checks) {
      if (check.wait > 0) {
        check.wait -= delta;
      } else {
        check.check();
        check.wait = check.interval;
      }
    }
    // Execute any pending actions
    this.pendingActions.forEach((action, key) => {
      action();
      this.pendingActions.delete(key);
    });
  }

  private messageHandlers = new Map<string, (data: any) => void>();
  public clearMessageHandlers() {
    this.messageHandlers.clear();
  }

  registerMessageHandler(path: string, handler: (data: any) => void) {
    this.messageHandlers.set(path, handler);
  }

  unregisterMessageHandler(path: string) {
    for (const key of this.messageHandlers.keys()) {
      if (key.startsWith(path)) {
        this.messageHandlers.delete(key);
      }
    }
  }

  public dispose(): void {
    this.proxy.terminate();
    super.dispose();
  }
}